import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAllPlansThunk } from "../../../../store/thunks/jewelleryPlan/planThunks";
import PlansTable from "./PlansTable";
import PlanCard from "../../../../components/JewelleryPlans/common/PlanCard";
import { PLAN_STATUS } from "../../../../constants/jewellery.constant";
import {
  GiftIcon,
  SearchIcon,
  FilterIcon,
  TableIcon,
  ViewGridIcon,
  CheckCircleIcon,
  CalendarIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";

const MaturedPlans = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    plans = [],
    loading,
    pagination = {},
  } = useSelector((state) => state.jewelleryPlan?.plans || {});
  const { selectedBranch } = useSelector(
    (state) => state.jewelleryPlan?.jewellery
  );

  const [viewMode, setViewMode] = useState("grid"); // grid or table
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: PLAN_STATUS.MATURED,
    branchId: selectedBranch,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("maturityDate"); // maturityDate, customerName, value

  useEffect(() => {
    dispatch(
      getAllPlansThunk({
        ...filters,
        page: currentPage,
        limit: 12,
        search: searchTerm,
        sortBy: sortBy,
      })
    );
  }, [dispatch, filters, currentPage, searchTerm, sortBy]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  const handleProcessRedemption = (planId) => {
    navigate(`/jewellery/${planId}/quote`);
  };

  // Calculate statistics
  const getStatistics = () => {
    const stats = {
      totalMatured: plans.length,
      pendingRedemption: plans.filter(
        (p) => p.status === PLAN_STATUS.MATURED && !p.redeemedOn
      ).length,
      redeemed: plans.filter((p) => p.status === PLAN_STATUS.REDEEMED).length,
      totalValue: plans.reduce((sum, p) => sum + (p.wallet?.valueInr || 0), 0),
    };
    return stats;
  };

  const stats = getStatistics();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">Matured Plans</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Plans ready for redemption or already redeemed
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm text-neutral-dark">Total Matured Value</p>
              <p className="text-2xl font-bold text-success">
                ₹{stats.totalValue.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-background rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarIcon className="h-10 w-10 text-secondary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-dark">
                Total Matured
              </p>
              <p className="text-2xl font-semibold text-text">
                {stats.totalMatured}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationIcon className="h-10 w-10 text-warning" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-dark">
                Pending Redemption
              </p>
              <p className="text-2xl font-semibold text-text">
                {stats.pendingRedemption}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-10 w-10 text-success" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-dark">Redeemed</p>
              <p className="text-2xl font-semibold text-text">
                {stats.redeemed}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <GiftIcon className="h-10 w-10 text-primary" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-neutral-dark">
                Redemption Rate
              </p>
              <p className="text-2xl font-semibold text-text">
                {stats.totalMatured > 0
                  ? `${Math.round(
                      (stats.redeemed / stats.totalMatured) * 100
                    )}%`
                  : "0%"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-background shadow rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-neutral-dark" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                placeholder="Search by plan number, customer name..."
                className="block w-full pl-10 pr-3 py-2 border border-neutral-light rounded-md focus:ring-primary focus:border-primary"
              />
            </div>
          </div>

          {/* Filters and View Toggle */}
          <div className="flex items-center space-x-2">
            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-neutral-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
            >
              <option value="maturityDate">Maturity Date</option>
              <option value="customerName">Customer Name</option>
              <option value="value">Wallet Value</option>
              <option value="status">Status</option>
            </select>

            {/* Status Filter */}
            <select
              value={filters.status || "all"}
              onChange={(e) =>
                handleFilterChange(
                  "status",
                  e.target.value === "all" ? null : e.target.value
                )
              }
              className="block w-full pl-3 pr-10 py-2 text-base border-neutral-light focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
            >
              <option value="all">All Matured</option>
              <option value={PLAN_STATUS.MATURED}>Pending Redemption</option>
              <option value={PLAN_STATUS.REDEEMED}>Already Redeemed</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex border border-neutral-light rounded-md">
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-2 text-sm font-medium rounded-l-md
                  ${
                    viewMode === "grid"
                      ? "bg-neutral-light text-text"
                      : "bg-background text-neutral-dark hover:text-text"
                  }`}
              >
                <ViewGridIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-2 text-sm font-medium rounded-r-md border-l border-neutral-light
                  ${
                    viewMode === "table"
                      ? "bg-neutral-light text-text"
                      : "bg-background text-neutral-dark hover:text-text"
                  }`}
              >
                <TableIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Plans Display */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-background shadow rounded-lg p-12 text-center">
          <GiftIcon className="h-12 w-12 text-neutral-dark mx-auto mb-4" />
          <p className="text-neutral-dark">No matured plans found</p>
          <p className="text-sm text-neutral-dark mt-2">
            Plans will appear here once they reach maturity
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan._id} className="relative">
              <PlanCard plan={plan} />
              {plan.status === PLAN_STATUS.MATURED && (
                <div className="absolute top-2 right-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProcessRedemption(plan._id);
                    }}
                    className="inline-flex items-center px-3 py-1 bg-success text-text-on-primary text-xs font-medium rounded-md hover:bg-success-dark"
                  >
                    <GiftIcon className="h-4 w-4 mr-1" />
                    Redeem
                  </button>
                </div>
              )}
              {plan.redeemedOn && (
                <div className="absolute top-2 right-2">
                  <span className="inline-flex items-center px-3 py-1 bg-primary-light text-primary-dark text-xs font-medium rounded-md">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Redeemed
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <PlansTable plans={plans} showRedemptionActions={true} />
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-background px-4 py-3 flex items-center justify-between border-t border-neutral-light sm:px-6 rounded-lg shadow">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-text">
                Showing page <span className="font-medium">{currentPage}</span>{" "}
                of <span className="font-medium">{pagination.pages}</span> (
                {pagination.total} total plans)
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-light bg-background text-sm font-medium text-neutral-dark hover:bg-neutral-light"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination.pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-light bg-background text-sm font-medium text-neutral-dark hover:bg-neutral-light"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Redemption Benefits Notice */}
      <div className="bg-warning-light border border-warning rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationIcon className="h-5 w-5 text-warning" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-warning-dark">
              Redemption Window Notice
            </h3>
            <div className="mt-2 text-sm text-warning-dark">
              <p>
                Plans should be redeemed within 60 days of maturity to avail
                full benefits. After this period, certain benefits may be
                forfeited.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaturedPlans;
